import { GetAllExpeditionResponseDtoExpedition } from "@/types/api/api-types";
import { useEffect } from "react";
import { formatDate } from "date-fns";
import { fr } from "date-fns/locale";
import { FileSpreadsheet, X } from "lucide-react";
import { ActionData } from "@/types/tableAction";
import { CountryBadge } from "@/components/shared/badge/country.badge";
import { Button } from "@/components/ui/button";
import { handleOpenDocument } from "@/lib/file.util";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { useTranslation } from "react-i18next";

type Props = {
  expedition: GetAllExpeditionResponseDtoExpedition;
  onClose: () => void;
  navigate: (path: string) => void;
  setActionData: (actionData: ActionData) => void;
};

export const ExpeditionMobileModal = ({ expedition, onClose, navigate, setActionData }: Props) => {
  const { t } = useTranslation("boExpeditions");
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const expeditionIdentifier = expedition.quotation
    ? `C-${expedition.quotation.identifier}`
    : `D-${expedition.customQuotation!.identifier}`;

  const client =
    expedition.deliveryInfo.company ||
    (expedition.deliveryInfo.firstName && expedition.deliveryInfo.lastName
      ? expedition.deliveryInfo.firstName + " " + expedition.deliveryInfo.lastName
      : "-");

  const quote = expedition.expeditionDocuments?.documents.find(
    (document) => document.type === FileTypeEnum.EXPEDITION_QUOTE,
  );

  const finalizeDisabled = expedition.customQuotation && !quote;

  const handleModifyCotation = () => {
    if (expedition.quotation) {
      navigate(`/cotations/${expedition.id}`);
    } else {
      navigate(`/custom-cotations/${expedition.id}`);
    }
  };

  return (
    <dialog className="fixed top-0 left-0 z-50 flex h-full w-full items-end justify-center overflow-auto bg-[#00000080] backdrop-blur-sm">
      <div className="flex w-full flex-col items-center justify-center gap-2 rounded-t-2xl bg-white p-6">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="w-[20px]"></div>
          <span className="text-sm font-medium text-gray-600">
            {t("pendingShipmentsMobileModal.title", { expeditionIdentifier })}
          </span>
          <button onClick={onClose} className="text-sm font-medium text-black">
            <X />
          </button>
        </div>
        <div className="flex w-full flex-row items-center justify-center gap-2">
          <span className="text-sm font-medium text-black">{client}</span>
          <span className="text-xs text-gray-600">
            {formatDate(expedition.createdAt, "dd MMM yyyy", {
              locale: fr,
            })}
          </span>
        </div>
        <span className="text-sm font-medium text-gray-600">
          {expedition.createdBy
            ? expedition.createdBy.firstName + " " + expedition.createdBy.lastName
            : "-"}
        </span>
        {expedition.customQuotation ? (
          quote ? (
            <Button variant="outline" onClick={() => handleOpenDocument(quote.path)}>
              <FileSpreadsheet />
              <p>{quote.name}</p>
            </Button>
          ) : (
            <Button disabled={true} variant="outline" onClick={() => {}}>
              <FileSpreadsheet />
              <p>{t("pendingShipmentsMobileModal.quoteInProgress")}</p>
            </Button>
          )
        ) : (
          <></>
        )}
        <CountryBadge country={expedition.deliveryInfo?.country || "-"} />
        <div className="flex w-full flex-row items-center justify-center gap-4 pt-3">
          <Button onClick={handleModifyCotation} variant="outline">
            {t("pendingShipmentsMobileModal.edit")}
          </Button>
          <Button
            onClick={() =>
              setActionData({
                expedition: expedition,
                action: "FINALIZE",
              })
            }
            disabled={finalizeDisabled}
          >
            {t("pendingShipmentsMobileModal.finalize")}
          </Button>
        </div>
        <button
          onClick={() =>
            setActionData({
              expedition: expedition,
              action: "DELETE",
            })
          }
          className="h-fit w-fit bg-transparent p-2 font-semibold text-red-600 focus:outline-hidden"
        >
          {t("pendingShipmentsMobileModal.delete")}
        </button>
      </div>
    </dialog>
  );
};
